import { useContext, useMemo } from 'react';
import { useStableValue } from '../../../../hooks/useStableValue';
import { UserContext } from '../../../UserContext';
import type { SecurityAction, SecurityDecision, SecurityDecisionBatch, SecurityTarget } from '../../types';
import { EntitySecurityContext } from '../contexts/EntitySecurityContext';
import { FeatureSecurityContext } from '../contexts/FeatureSecurityContext';
import { evaluateSecurityDecisions, type SecurityEvaluationContext } from './securityEvaluationUtils';

/**
 * Evaluates security decisions for one or more actions using the current user, feature, and entity security context.
 *
 * Supports both a single action result and batched action results via overloads.
 *
 * @remarks
 * This hook stabilizes the `action(s)` and `target` using `useStableValue` to prevent unnecessary re-evaluations.
 * No need to memoize them manually. Inline objects or arrays can be passed directly.
 */
export function useSecurity(action: SecurityAction, target?: SecurityTarget): SecurityDecision;
export function useSecurity(actions: SecurityAction[], target?: SecurityTarget): SecurityDecisionBatch;
export function useSecurity(
    actionOrActions: SecurityAction | SecurityAction[],
    target?: SecurityTarget
): SecurityDecision | SecurityDecisionBatch {
    const { userProfile, application, activeOrganizationAccount } = useContext(UserContext);
    const featureContextValue = useContext(FeatureSecurityContext);
    const entityContextValue = useContext(EntitySecurityContext);

    const stableActionOrActions = useStableValue(actionOrActions);
    const stableTarget = useStableValue(target);

    const securityDecision = useMemo<SecurityDecision | SecurityDecisionBatch>(() => {
        const featureNames = featureContextValue?.featureNames ?? [];
        const entitySecurityContexts = entityContextValue?.entitySecurityContexts ?? [];
        const evaluationContext: SecurityEvaluationContext = {
            userProfile,
            application,
            activeOrganizationAccount,
            featureNames,
            entitySecurityContexts
        };

        return evaluateSecurityDecisions(stableActionOrActions, stableTarget, evaluationContext);
    }, [
        stableActionOrActions,
        activeOrganizationAccount,
        application,
        entityContextValue?.entitySecurityContexts,
        featureContextValue?.featureNames,
        stableTarget,
        userProfile
    ]);

    return securityDecision;
}
